import { useMemo, useState, type FormEvent } from 'react';
import { Button, Field, Input, Select } from '@biovet/ui';
import { ApiError, apiSend, useResource } from '../lib/api.js';

const LAST_USER_KEY = 'biovet.lastUserId';

interface ClientInfo {
  ip: string | null;
  city: string | null;
}

interface CompanyOption {
  code: string;
  name: string;
  legalName: string;
}

export function LoginScreen({ onSignIn }: { onSignIn: () => void }) {
  const [remembered] = useState(readLastUserId);
  const [userId, setUserId] = useState(remembered ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<{ field: 'user' | 'password'; message: string } | null>(null);
  const [signingIn, setSigningIn] = useState(false);

  const clientRes = useResource<ClientInfo>('/client-info');
  const companiesRes = useResource<{ companies: CompanyOption[] }>('/companies');
  const companies = companiesRes.data?.companies ?? [];

  const greeting = useMemo(() => pickGreeting(karachiHour(), nameFor(remembered)), [remembered]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const typed = userId.trim();

    if (!typed) {
      setError({ field: 'user', message: 'Enter your user ID.' });
      return;
    }
    if (!password) {
      setError({ field: 'password', message: 'Enter your password.' });
      return;
    }

    setError(null);
    setSigningIn(true);
    try {
      await apiSend<{ email: string; fullName: string }>('POST', '/login', {
        userId: typed,
        password,
      });
      rememberUserId(typed.toLowerCase());
      onSignIn();
    } catch (cause) {
      setSigningIn(false);
      setError({
        field: 'password',
        message:
          cause instanceof ApiError
            ? cause.message
            : 'Cannot reach the API. Start it with `pnpm api`.',
      });
    }
  };

  return (
    <div className="min-h-screen overflow-y-auto bg-[var(--bg)] px-5 py-12">
      <div className="mx-auto flex w-full max-w-[400px] flex-col items-center">
        <header className="bb-rise-in flex flex-col items-center text-center">
          <div className="flex items-center gap-2.5">
            <div className="grid h-[30px] w-[30px] place-items-center rounded-lg bg-[var(--accent-solid)] text-[15px] font-bold tracking-tight text-[var(--accent-ink)]">
              b
            </div>
            <div className="flex flex-col items-start leading-none">
              <span className="text-[16px] font-semibold tracking-tight">biovet</span>
              <span className="bb-eyebrow">Accounting</span>
            </div>
          </div>

          <p className="mt-4 mb-0 text-[12.5px] text-[var(--ink-2)]">
            Double-entry books · PKR · financial year 2026
          </p>
        </header>

        <form
          onSubmit={(e) => void submit(e)}
          className="bb-rise-in mt-7 w-full rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] p-7 shadow-[var(--shadow)]"
          style={{ animationDelay: '0.08s' }}
        >
          <h2 className="m-0 text-[22px] font-semibold tracking-tight">Sign in</h2>
          <p className="bb-num mt-2 mb-0 text-[12.5px] text-[var(--ink-2)]">
            {describeConnection(clientRes.data, clientRes.loading)}
          </p>

          <div className="flex flex-col gap-3.5 pt-6">
            <Field label="Company" htmlFor="login-company">
              <Select id="login-company">
                {companies.length === 0 ? (
                  <option>{companiesRes.loading ? 'Loading…' : 'Biovet (Pvt.) Limited'}</option>
                ) : (
                  companies.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.legalName}
                    </option>
                  ))
                )}
              </Select>
            </Field>

            <Field
              label="User ID"
              htmlFor="login-user"
              error={error?.field === 'user' ? error.message : undefined}
            >
              {}
              <Input
                id="login-user"
                autoComplete="username"
                autoFocus
                value={userId}
                invalid={error?.field === 'user'}
                onChange={(e) => setUserId(e.target.value)}
              />
            </Field>

            {}
            <Field
              label="Password"
              htmlFor="login-password"
              error={error?.field === 'password' ? error.message : undefined}
            >
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                invalid={error?.field === 'password'}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>

            <div className="flex justify-end pt-0.5">
              <a href="#forgot" className="text-[13px] text-[var(--accent)] hover:underline">
                Forgot password
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={signingIn}
              className="mt-1.5"
            >
              {signingIn ? 'Opening your books…' : 'Sign in'}
            </Button>
          </div>

          <div className="flex items-start gap-2.5 pt-5 text-[12px] leading-relaxed text-[var(--ink-3)] text-pretty">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              className="mt-0.5 shrink-0"
              aria-hidden="true"
            >
              <path d="M12 3l7 4v6c0 4-3 6.5-7 8-4-1.5-7-4-7-8V7z" />
            </svg>
            <span>
              Your password is never stored and nobody here can read it. Everything you enter is
              saved under your name, and an entry once posted can be corrected but never quietly
              changed.
            </span>
          </div>
        </form>

        <p
          className="bb-rise-in mt-7 max-w-[38ch] text-center text-[13.5px] leading-relaxed text-[var(--ink-2)] text-pretty"
          style={{ animationDelay: '0.16s' }}
        >
          {greeting}
        </p>
      </div>
    </div>
  );
}


function describeConnection(info: ClientInfo | undefined, loading: boolean): string {
  if (loading) return 'Checking your connection…';
  if (!info?.ip) return 'Signing in from this device';
  if (info.ip === '::1' || info.ip === '127.0.0.1') return 'This device · local network';
  return info.city ? `${info.ip} · ${info.city}` : info.ip;
}


type Band = 'morning' | 'afternoon' | 'evening' | 'night';

function karachiHour(): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    hour12: false,
    timeZone: 'Asia/Karachi',
  }).format(new Date());
  return Number(hour) % 24;
}

function bandFor(hour: number): Band {
  if (hour < 5) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  if (hour < 22) return 'evening';
  return 'night';
}

const GREETINGS: Record<Band, string[]> = {
  morning: [
    'Good morning{name}. The books are where you left them.',
    'Morning{name} — yesterday is closed, today is open.',
    'Good morning{name}. A quiet ledger is a good way to start.',
  ],
  afternoon: [
    'Good afternoon{name}. Half the day is still postable.',
    'Afternoon{name} — plenty of period left.',
    'Good afternoon{name}. Back for the second half.',
  ],
  evening: [
    'Good evening{name}. Worth clearing the queue before you go.',
    'Evening{name} — a good hour for a trial balance.',
    'Good evening{name}. Last entries of the day.',
  ],
  night: [
    'Working late{name}? The ledger keeps your hours.',
    'Late night{name}. Whatever you post will still be here tomorrow.',
    'It is late{name} — post carefully, or leave it as a draft.',
  ],
};

function pickGreeting(hour: number, name: string | null): string {
  const options = GREETINGS[bandFor(hour)];
  const line = options[Math.floor(Math.random() * options.length)] ?? options[0]!;
  return line.replace('{name}', name ? `, ${name}` : '');
}

function nameFor(userId: string | null): string | null {
  const best = (userId ?? '')
    .split(/[.\-_@]/)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)[0];
  if (!best || best.length < 3) return null;
  return best.charAt(0).toUpperCase() + best.slice(1);
}

function readLastUserId(): string | null {
  try {
    return window.localStorage.getItem(LAST_USER_KEY);
  } catch {
    return null;
  }
}

function rememberUserId(userId: string): void {
  try {
    window.localStorage.setItem(LAST_USER_KEY, userId);
  } catch {
  }
}
